import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { type Agent, type AgentId, agentsConfig } from '@langmail/agents'
import { languageOptions, useAuth, useTRPC } from '@langmail/shared-ui'

import { Screen } from '@/components/screen'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useProfile } from '@/features/auth/hooks/use-profile'
import { useToast } from '@/hooks/ui/use-toast'

const INCLUDE_IN_SUMMARY = 'includeInSummary'

interface AgentState {
  agentId: string
  config: Record<string, unknown>
}

// The option's stored value, or its default when the user never changed it.
function includeInSummary(state: AgentState | undefined, agentDef: Agent): boolean {
  const option = agentDef.options.find((opt) => opt.key === INCLUDE_IN_SUMMARY)
  if (!option) {
    return false
  }
  return (state?.config[INCLUDE_IN_SUMMARY] ?? option.default) as boolean
}

// The Morning Brief tab: which language the brief is written in, and which
// categories it lists after the To Dos.
export function MorningBrief() {
  const { user } = useAuth()
  const { profile, updateProfile } = useProfile()
  const { toast } = useToast()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const { data: agentStates } = useQuery(trpc.agents.list.queryOptions())
  const stateMap = new Map<string, AgentState>((agentStates ?? []).map((state) => [state.agentId, state]))

  const updateConfigMutation = useMutation({
    ...trpc.agents.updateConfig.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.agents.list.queryKey() })
      toast({ title: 'Settings updated' })
    },
    onError: () => {
      toast({ title: 'Failed to update settings', variant: 'destructive' })
    },
  })

  const updateLanguageMutation = useMutation({
    ...trpc.user.updateLanguage.mutationOptions(),
    onSuccess: (_data, variables) => {
      updateProfile({ language: variables.language })
      toast({
        title: 'Language updated',
        description: 'Your language preference has been saved.',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update language. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleLanguageChange = (newLanguage: string) => {
    if (!user) {
      return
    }
    updateLanguageMutation.mutate({ language: newLanguage })
  }

  const handleIncludeChange = (agentId: AgentId, value: boolean) => {
    const state = stateMap.get(agentId)
    if (!state) {
      return
    }
    updateConfigMutation.mutate({
      agentId,
      config: { ...state.config, [INCLUDE_IN_SUMMARY]: value },
    })
  }

  const agentsWithSummary = agentsConfig.agents
    .filter((agent) => agent.options.some((opt) => opt.key === INCLUDE_IN_SUMMARY))
    .map((agentDef) => ({ agentDef, state: stateMap.get(agentDef.id) }))

  return (
    <Screen title="Morning Brief">
      <div>
        <div className="mb-8">
          <label
            className="mb-2 block font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]"
            htmlFor="morning-brief-language"
          >
            Language
          </label>
          <Select disabled={!profile} onValueChange={handleLanguageChange} value={profile?.language || 'en'}>
            <SelectTrigger id="morning-brief-language">
              <SelectValue placeholder="Select your language" />
            </SelectTrigger>
            <SelectContent>
              {languageOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {agentsWithSummary.length > 0 && (
          <div className="mb-8">
            <div className="mb-2 block font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
              Include in Morning Brief
            </div>
            <p className="mb-5 text-muted-foreground text-sm italic">
              Emails needing your attention are always shown first.
            </p>
            <div className="space-y-3">
              {/* Static To-Do item - always enabled */}
              <div className="flex items-center gap-3">
                <Checkbox checked={true} className="size-5" disabled={true} id="todo-include-summary" />
                <label className="cursor-default text-[15px] text-muted-foreground" htmlFor="todo-include-summary">
                  To Do
                </label>
              </div>

              {agentsWithSummary.map(({ agentDef, state }) => (
                <div className="flex items-center gap-3" key={agentDef.id}>
                  <Checkbox
                    checked={includeInSummary(state, agentDef)}
                    className="size-5"
                    disabled={!state || updateConfigMutation.isPending}
                    id={`${agentDef.id}-include-summary`}
                    onCheckedChange={(checked) => handleIncludeChange(agentDef.id, checked === true)}
                  />
                  <label className="cursor-pointer text-[15px]" htmlFor={`${agentDef.id}-include-summary`}>
                    {agentDef.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Screen>
  )
}
